<template>
  <div class="card border-0 shadow-none rounded-3 mb-4 settings-card">
    <div class="card-body p-4 p-xl-40">
      <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <span class="settings-chip">{{ $t("system_settings_page.second_screen") }}</span>
          <h4 class="mb-2 text-title fw-semibold mt-3">
            {{ $t("system_settings_page.customer_display_promotions") }}
          </h4>
          <p class="text-optional mb-0 settings-subtitle">
            {{ $t("system_settings_page.customer_display_description") }}
          </p>
        </div>
        <button type="button" class="btn style-five px-4" :disabled="isSaving" @click="$emit('submit')">
          <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
          {{ $t("system_settings_page.save_display_content") }}
        </button>
      </div>

      <div class="poster-editor-grid">
        <article
          v-for="(poster, index) in posters"
          :key="`customer-display-poster-${index}`"
          class="poster-editor-card"
        >
          <div class="poster-editor-head">
            <span class="poster-number">
              {{ $t("system_settings_page.poster_number", { number: index + 1 }) }}
            </span>
            <button type="button" class="btn btn-light btn-sm" @click="resetPoster(index)">
              {{ $t("system_settings_page.reset") }}
            </button>
          </div>

          <div class="row g-3">
            <div class="col-12">
              <label class="settings-label">{{ $t("system_settings_page.promotion_image") }}</label>
              <input
                type="file"
                class="form-control settings-input settings-file-input mb-2"
                accept="image/*"
                @change="handlePosterFileChange(index, $event)"
              />
              <input
                :value="poster.image"
                type="text"
                class="form-control settings-input"
                :placeholder="$t('system_settings_page.promotion_image_placeholder')"
                @input="updatePoster(index, 'image', ($event.target as HTMLInputElement).value)"
              />
              <p class="settings-help mb-0 mt-2">
                {{ $t("system_settings_page.promotion_image_help") }}
              </p>
            </div>

            <div class="col-12">
              <label class="settings-label">{{ $t("system_settings_page.promotion_caption") }}</label>
              <input
                :value="poster.caption"
                type="text"
                maxlength="90"
                class="form-control settings-input"
                :placeholder="$t('system_settings_page.promotion_caption_placeholder')"
                @input="updatePoster(index, 'caption', ($event.target as HTMLInputElement).value)"
              />
            </div>
          </div>

          <div class="poster-preview">
            <img
              v-if="posterImageUrl(poster)"
              :src="posterImageUrl(poster)"
              :alt="posterCaption(poster)"
            />
            <div v-else class="poster-preview-empty">
              {{ $t("system_settings_page.promotion_image") }}
            </div>
            <div class="poster-preview-caption">
              {{ posterCaption(poster) || $t("system_settings_page.preview_caption") }}
            </div>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onUnmounted, PropType, ref } from "vue";

import type { CustomerDisplayPoster, Setting } from "@/services/setting";
import { buildAssetUrl } from "@/utils/settings";
import { DEFAULT_CUSTOMER_DISPLAY_POSTERS } from "@/utils/customerDisplayPosters";

type PosterField = keyof CustomerDisplayPoster;

export default defineComponent({
  name: "CustomerDisplaySettings",
  emits: ["poster-image-change", "submit", "update:settings"],
  props: {
    settings: {
      type: Object as PropType<Setting>,
      required: true,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const posters = computed(() => props.settings.customer_display_posters || DEFAULT_CUSTOMER_DISPLAY_POSTERS);
    const posterPreviews = ref<Record<number, string>>({});

    const posterImageUrl = (poster: CustomerDisplayPoster) =>
      posterPreviews.value[posters.value.indexOf(poster)] || buildAssetUrl(poster.image || "");

    const posterCaption = (poster: CustomerDisplayPoster) =>
      String(poster.caption || poster.title || poster.text || "").trim();

    const toPosterPayload = (poster: CustomerDisplayPoster): CustomerDisplayPoster => ({
      image: String(poster.image || "").trim(),
      caption: posterCaption(poster),
    });

    const patchSettings = (nextPosters: CustomerDisplayPoster[]) => {
      emit("update:settings", {
        ...props.settings,
        customer_display_posters: nextPosters.map(toPosterPayload),
      });
    };

    const updatePoster = (
      index: number,
      field: PosterField,
      value: string
    ) => {
      const nextPosters = posters.value.map((poster, posterIndex) =>
        posterIndex === index
          ? {
              ...poster,
              [field]: value,
            }
          : { ...poster }
      );

      patchSettings(nextPosters);
    };

    const clearPosterPreview = (index: number) => {
      const preview = posterPreviews.value[index];
      if (preview) {
        URL.revokeObjectURL(preview);
      }
      delete posterPreviews.value[index];
    };

    const handlePosterFileChange = (index: number, event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0] || null;
      clearPosterPreview(index);

      if (file) {
        posterPreviews.value[index] = URL.createObjectURL(file);
      }

      emit("poster-image-change", index, file);
    };

    const resetPoster = (index: number) => {
      clearPosterPreview(index);
      emit("poster-image-change", index, null);

      const nextPosters = posters.value.map((poster, posterIndex) =>
        posterIndex === index
          ? { ...DEFAULT_CUSTOMER_DISPLAY_POSTERS[index] }
          : { ...poster }
      );

      patchSettings(nextPosters);
    };

    onUnmounted(() => {
      Object.keys(posterPreviews.value).forEach((index) =>
        clearPosterPreview(Number(index))
      );
    });

    return {
      posters,
      posterCaption,
      posterImageUrl,
      handlePosterFileChange,
      updatePoster,
      resetPoster,
    };
  },
});
</script>

<style scoped>
.settings-card {
  background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
}

.settings-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: rgba(96, 93, 255, 0.12);
  color: #605dff;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 14px;
}

.settings-subtitle {
  max-width: 720px;
}

.settings-help {
  color: #64748b;
  font-size: 12px;
}

.poster-editor-grid {
  display: grid;
  gap: 22px;
}

.poster-editor-card {
  border: 1px solid #e6ebff;
  border-radius: 28px;
  background: #f9fbff;
  padding: 22px;
}

.poster-editor-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.poster-number {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 12px;
  font-weight: 800;
  padding: 8px 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.settings-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.settings-input {
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #ffffff;
  color: #0f172a;
  box-shadow: none;
  padding: 14px 18px;
}

.settings-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.settings-file-input {
  padding: 12px 14px;
}

.poster-preview {
  margin-top: 20px;
  position: relative;
  min-height: 260px;
  overflow: hidden;
  border-radius: 24px;
  background: #111827;
  color: #ffffff;
}

.poster-preview img {
  width: 100%;
  height: 260px;
  object-fit: cover;
  display: block;
}

.poster-preview-empty {
  display: grid;
  min-height: 260px;
  place-items: center;
  color: rgba(255, 255, 255, 0.72);
  font-weight: 700;
}

.poster-preview-caption {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 16px;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.76);
  color: #ffffff;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.35;
  backdrop-filter: blur(8px);
}

@media (max-width: 767px) {
  .poster-editor-card {
    border-radius: 20px;
    padding: 18px;
  }
}
</style>
